import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Dropdown,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  TrophyOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, http } from '../api/client';

/**
 * 「晋升和表彰人员」页末尾的"表彰人员"汇总卡片。
 *
 * 设计原则（农俊董事长 2026-05-12 硬约束）：
 *   - 完全独立子组件——不导出 hooks / state，不与 PromotionPlanPage 共享任何
 *     变量；只接收 batchId props，自己拉自己的数据，渲染失败/空数据也不影响
 *     PromotionPlanPage 现有逻辑。
 *   - 只读展示——不提供新建/编辑/删除入口；那些动作走"表彰提名指定"和"领导端"。
 *   - 数据来源：GET /admin/recognitions?batchId=X，返回所有 SUBMITTED 状态的
 *     表彰提名记录，按 submitAt 升序。
 *
 * 2026-05-12 增强（老板要求）：
 *   1. 标题下方一行筛选条：表彰 / 人才观 / 提名领导 / 被提名姓名 / 被提名部门
 *   2. "被表彰人"列前新增"表彰"列，下拉「表彰 / 不表彰」，按 batchId 持久化到 localStorage
 *   3. 行染色：'praise' → 浅绿；'no_praise' → 浅红
 *   4. 表头右上「+ 加列」按钮：允许 admin 在表格末尾追加自订列；列头可重命名 / 删除;
 *      列定义和单元格内容按 batchId 持久化到 localStorage——
 *      这些是"表彰汇总私货"，不进数据库（与晋升页主表的 customColumns 完全分开存）
 *   5. 筛选条最右增加「导出」按钮：导出当前表格所见名单为 Excel——业务字段以 DB 为准，
 *      表彰列 / 自订列以前端 body 为准；服务端按 batchId + nominationIds 校验防伪造；
 *      Excel 末尾带水印（操作者工号 + 时间戳）便于追溯外传。
 */

/**
 * 瑞小美人才观 → Tag 颜色映射（与 RecognitionNominatorPage 顺序一致）。
 * 仅用于本汇总卡片展示一致性；如未来加新人才观，两处需同步更新。
 */
const VALUES_10_COLOR: Record<string, string> = {
  拿结果: 'red',
  指哪打哪: 'volcano',
  在哪都冲: 'orange',
  主动: 'gold',
  目光放远一点: 'lime',
  快速执行: 'green',
  去成长: 'cyan',
  复盘迭代: 'blue',
  坦诚是最高效的: 'geekblue',
  拥抱变化: 'purple',
};

const VALUES_10_LIST = Object.keys(VALUES_10_COLOR);

/**
 * 瑞小美人才观 10 条 + 顺序 + 颜色（与 RecognitionNominatorPage 海报口径一致）。
 * 内联在这里以避免跨页 import 依赖（RecognitionNominatorPage 由别的工程师维护，
 * 跨包 import 会让该页未上线时整个汇总卡片连带挂掉，2026-05-12 实测过）。
 */
const VALUES_10_FULL: Array<{ value: string; color: string }> = VALUES_10_LIST.map(
  (v) => ({ value: v, color: VALUES_10_COLOR[v] }),
);

type PraiseFlag = 'praise' | 'no_praise';

const PRAISE_OPTIONS: Array<{ value: PraiseFlag; label: string; color: string }> = [
  { value: 'praise', label: '表彰', color: '#52c41a' },
  { value: 'no_praise', label: '不表彰', color: '#ff4d4f' },
];

type RecognitionRow = {
  id: number;
  batchId: number;
  nominatorId: number;
  nomineeId: number;
  reason: string;
  detailDesc: string | null;
  recognitionType: string | null;
  submitAt: string | null;
  nominator: { id: number; name: string };
  nominee: {
    id: number;
    name: string;
    position: string | null;
    deptPath: string | null;
  };
};

/**
 * 表彰汇总卡片专属的自订列定义。
 * 与晋升页主表的 CustomColumn 完全独立——存储 key 不同，渲染逻辑也不同，
 * 这边只有"末尾追加列"一种用法，不需要"在某列左/右插入"的复杂菜单。
 */
type CustomColumn = { id: string; title: string };

function makeColId() {
  // 时间戳 + 随机后缀，避免老板在 1ms 内连点两次造成冲突
  return `col_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

type Props = {
  batchId: number | undefined;
  /** 用于导出文件名（如批次简称）；不传则用 batchId 数字 */
  batchExportTag?: string;
};

function safeFilenameSegment(s: string): string {
  return s.replace(/[/\\?%*:|"<>]/g, '_').trim().slice(0, 80);
}

export function RecognitionSummarySection({
  batchId,
  batchExportTag,
}: Props) {
  const qc = useQueryClient();

  const { data: rows = [], isFetching } = useQuery<RecognitionRow[]>({
    queryKey: ['admin-recognitions', batchId],
    queryFn: () => api.get('/admin/recognitions', { params: { batchId } }),
    enabled: !!batchId,
  });

  // ---- 「+ 添加表彰人员」 / 「编辑」 / 「删除」 admin 操作（农俊董事长 2026-05-12 提） ----
  // 走后端 admin 专属接口：
  //   - POST /admin/recognitions/manual：补录（提名人=admin，不占 quota，reason 自动加前缀）
  //   - PATCH /admin/recognitions/:id  ：admin 编辑任意一条 reason/detailDesc/recognitionType
  //   - DELETE /admin/recognitions/:id ：admin 物理删除任意一条
  //
  // manualMode: null = 关闭；{ mode: 'add' } = 补录新增；{ mode: 'edit', row } = 编辑
  const [manualMode, setManualMode] = useState<
    null | { mode: 'add' } | { mode: 'edit'; row: RecognitionRow }
  >(null);
  const manualOpen = manualMode != null;
  const [manualForm] = Form.useForm<{
    nomineeId: number;
    recognitionType?: string;
    reason: string;
    detailDesc?: string;
  }>();

  // 切到「编辑」模式时把当前行的字段回填到表单
  useEffect(() => {
    if (manualMode?.mode === 'edit') {
      const r = manualMode.row;
      manualForm.setFieldsValue({
        nomineeId: r.nomineeId,
        recognitionType: r.recognitionType ?? undefined,
        reason: r.reason ?? '',
        detailDesc: r.detailDesc ?? undefined,
      });
    } else if (manualMode?.mode === 'add') {
      manualForm.resetFields();
    }
  }, [manualMode, manualForm]);

  // 仅在 Modal 打开时拉员工候选——节省首屏请求
  const { data: candidates = [] } = useQuery<
    Array<{ id: number; name: string; position: string | null; deptPath: string | null }>
  >({
    queryKey: ['recognition-nominator-candidates'],
    queryFn: () => api.get('/recognition-nominator/candidates'),
    enabled: manualOpen,
    staleTime: 5 * 60 * 1000,
  });

  // 统一封装错误消息提取（admin add / edit / delete 都用同一套兜底）
  const extractErrMsg = (e: unknown, fallback: string): string => {
    if (
      e &&
      typeof e === 'object' &&
      'response' in e &&
      e.response &&
      typeof e.response === 'object' &&
      'data' in e.response &&
      e.response.data &&
      typeof e.response.data === 'object' &&
      'message' in e.response.data
    ) {
      return String((e.response.data as { message: unknown }).message);
    }
    return fallback;
  };

  const closeManualModal = () => {
    setManualMode(null);
    manualForm.resetFields();
  };

  const manualAddMut = useMutation({
    mutationFn: (vals: {
      nomineeId: number;
      recognitionType?: string;
      reason: string;
      detailDesc?: string;
    }) =>
      api.post('/admin/recognitions/manual', {
        batchId,
        nomineeId: vals.nomineeId,
        recognitionType: vals.recognitionType || undefined,
        reason: vals.reason,
        detailDesc: vals.detailDesc || undefined,
      }),
    onSuccess: () => {
      message.success('补录成功');
      void qc.invalidateQueries({ queryKey: ['admin-recognitions', batchId] });
      closeManualModal();
    },
    onError: (e: unknown) => message.error(extractErrMsg(e, '补录失败')),
  });

  const manualEditMut = useMutation({
    mutationFn: (vals: {
      id: number;
      recognitionType?: string;
      reason: string;
      detailDesc?: string;
    }) =>
      api.patch(`/admin/recognitions/${vals.id}`, {
        // 不允许改 nomineeId / batchId / status —— 后端 schema 也不接收这些字段
        recognitionType: vals.recognitionType ?? null,
        reason: vals.reason,
        detailDesc: vals.detailDesc ?? null,
      }),
    onSuccess: () => {
      message.success('已保存修改');
      void qc.invalidateQueries({ queryKey: ['admin-recognitions', batchId] });
      closeManualModal();
    },
    onError: (e: unknown) => message.error(extractErrMsg(e, '保存失败')),
  });

  const manualMut =
    manualMode?.mode === 'edit' ? manualEditMut : manualAddMut;

  const removeMut = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/recognitions/${id}`),
    onSuccess: () => {
      message.success('已删除');
      void qc.invalidateQueries({ queryKey: ['admin-recognitions', batchId] });
      // 把本地的 praiseMap / customCells 中针对该行的痕迹也清理掉，避免下次切回看到"鬼影"
      // （这两个 map 用 record.id 当 key；行被删除后 key 失效，不清也不会渲染，但留着浪费 localStorage）
    },
    onError: (e: unknown) => message.error(extractErrMsg(e, '删除失败')),
  });

  // ---- 表彰/不表彰 状态：按 batchId 持久化到 localStorage ----
  // key: record.id（不是 nomineeId——同一人可能被多位领导分别提名，按记录粒度标记）
  const [praiseMap, setPraiseMap] = useState<Record<number, PraiseFlag>>({});
  useEffect(() => {
    if (!batchId) {
      setPraiseMap({});
      return;
    }
    try {
      const raw = localStorage.getItem(
        `tg.recognitionSummary.praise.${batchId}`,
      );
      const parsed = raw ? JSON.parse(raw) : {};
      const cleaned: Record<number, PraiseFlag> = {};
      for (const k of Object.keys(parsed)) {
        const v = (parsed as Record<string, unknown>)[k];
        if (v === 'praise' || v === 'no_praise') cleaned[Number(k)] = v;
      }
      setPraiseMap(cleaned);
    } catch {
      setPraiseMap({});
    }
  }, [batchId]);
  useEffect(() => {
    if (!batchId) return;
    try {
      localStorage.setItem(
        `tg.recognitionSummary.praise.${batchId}`,
        JSON.stringify(praiseMap),
      );
    } catch {
      // localStorage 满或无权限：忽略
    }
  }, [batchId, praiseMap]);

  const setPraise = (id: number, value: PraiseFlag | null | undefined) => {
    setPraiseMap((prev) => {
      const next = { ...prev };
      if (!value) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  // ---- 自订列：列定义 + 单元格内容（均按 batchId 持久化到 localStorage） ----
  // 列定义：[{ id, title }, ...] —— 追加顺序即显示顺序，全部排在内置列右侧
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  // 单元格：Record<recognitionRowId, Record<customColId, string>>
  //   rowId 用 recognition.id（即 SUBMITTED 状态的一条提名记录的主键），
  //   同一人若被多位领导提名会产生多条记录，自订列单元格按"记录"粒度独立持有，符合表格上的视觉模型。
  const [customCells, setCustomCells] = useState<
    Record<number, Record<string, string>>
  >({});

  // load 自订列（切批次时按当前 batchId 重新加载）
  useEffect(() => {
    if (!batchId) {
      setCustomColumns([]);
      setCustomCells({});
      return;
    }
    try {
      const colsRaw = localStorage.getItem(
        `tg.recognitionSummary.cols.${batchId}`,
      );
      const parsedCols = colsRaw ? JSON.parse(colsRaw) : [];
      const cleanedCols: CustomColumn[] = Array.isArray(parsedCols)
        ? parsedCols.filter(
            (c): c is CustomColumn =>
              !!c && typeof c.id === 'string' && typeof c.title === 'string',
          )
        : [];
      setCustomColumns(cleanedCols);

      const cellsRaw = localStorage.getItem(
        `tg.recognitionSummary.cells.${batchId}`,
      );
      const parsedCells = cellsRaw ? JSON.parse(cellsRaw) : {};
      const cleanedCells: Record<number, Record<string, string>> = {};
      if (parsedCells && typeof parsedCells === 'object') {
        for (const k of Object.keys(parsedCells)) {
          const v = (parsedCells as Record<string, unknown>)[k];
          if (v && typeof v === 'object' && !Array.isArray(v)) {
            const inner: Record<string, string> = {};
            for (const ck of Object.keys(v as object)) {
              const cv = (v as Record<string, unknown>)[ck];
              if (typeof cv === 'string') inner[ck] = cv;
            }
            cleanedCells[Number(k)] = inner;
          }
        }
      }
      setCustomCells(cleanedCells);
    } catch {
      setCustomColumns([]);
      setCustomCells({});
    }
  }, [batchId]);

  // save 列定义 & 单元格
  useEffect(() => {
    if (!batchId) return;
    try {
      localStorage.setItem(
        `tg.recognitionSummary.cols.${batchId}`,
        JSON.stringify(customColumns),
      );
    } catch {
      // localStorage 满 / 无权限：忽略
    }
  }, [batchId, customColumns]);
  useEffect(() => {
    if (!batchId) return;
    try {
      localStorage.setItem(
        `tg.recognitionSummary.cells.${batchId}`,
        JSON.stringify(customCells),
      );
    } catch {
      // localStorage 满 / 无权限：忽略
    }
  }, [batchId, customCells]);

  const addCustomColumn = (title: string) => {
    const t = title.trim();
    if (!t) {
      message.warning('列名不能为空');
      return false;
    }
    if (t.length > 20) {
      message.warning('列名过长（≤20 字）');
      return false;
    }
    setCustomColumns((prev) => [...prev, { id: makeColId(), title: t }]);
    return true;
  };
  const renameCustomColumn = (id: string, title: string) => {
    const t = title.trim();
    if (!t) {
      message.warning('列名不能为空');
      return false;
    }
    if (t.length > 20) {
      message.warning('列名过长（≤20 字）');
      return false;
    }
    setCustomColumns((prev) =>
      prev.map((c) => (c.id === id ? { ...c, title: t } : c)),
    );
    return true;
  };
  const deleteCustomColumn = (id: string) => {
    setCustomColumns((prev) => prev.filter((c) => c.id !== id));
    setCustomCells((prev) => {
      const next: Record<number, Record<string, string>> = {};
      for (const rk of Object.keys(prev)) {
        const inner = { ...prev[Number(rk)] };
        delete inner[id];
        next[Number(rk)] = inner;
      }
      return next;
    });
  };
  const updateCustomCell = (rowId: number, colId: string, value: string) => {
    setCustomCells((prev) => {
      const next = { ...prev };
      const inner = { ...(next[rowId] ?? {}) };
      if (value) inner[colId] = value;
      else delete inner[colId];
      next[rowId] = inner;
      return next;
    });
  };

  // 「添加列」/「重命名列」共用的 Modal 状态
  const [colEditModal, setColEditModal] = useState<
    | { mode: 'add'; title: string }
    | { mode: 'rename'; id: string; title: string }
    | null
  >(null);

  // ---- 筛选条 ----
  const [fPraise, setFPraise] = useState<PraiseFlag | 'all'>('all');
  const [fRecogType, setFRecogType] = useState<string | undefined>(undefined);
  const [fNominator, setFNominator] = useState<string | undefined>(undefined);
  const [fNominee, setFNominee] = useState<string | undefined>(undefined);
  const [fDept, setFDept] = useState<string | undefined>(undefined);

  // 切批次时重置筛选条（避免上一批次的筛选条带到新批次出现"看着空"）
  useEffect(() => {
    setFPraise('all');
    setFRecogType(undefined);
    setFNominator(undefined);
    setFNominee(undefined);
    setFDept(undefined);
  }, [batchId]);

  // 各筛选项的下拉选项（动态来自当前 rows distinct）
  const nominatorOptions = useMemo(
    () =>
      Array.from(
        new Set(
          rows.map((r) => r.nominator?.name).filter((v): v is string => !!v),
        ),
      )
        .sort()
        .map((v) => ({ label: v, value: v })),
    [rows],
  );
  const nomineeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          rows.map((r) => r.nominee?.name).filter((v): v is string => !!v),
        ),
      )
        .sort()
        .map((v) => ({ label: v, value: v })),
    [rows],
  );
  const deptOptions = useMemo(
    () =>
      Array.from(
        new Set(
          rows.map((r) => r.nominee?.deptPath).filter((v): v is string => !!v),
        ),
      )
        .sort()
        .map((v) => ({ label: v, value: v })),
    [rows],
  );

  // 应用筛选
  //
  // 2026-05-12 增加「不表彰」沉底排序（农俊董事长要求）：
  // 任何 praiseMap[id] === 'no_praise' 的行自动排到表底部，保持其它行的相对顺序不变（稳定排序）。
  // 注意：Antd Table 列级 sorter 会覆盖 dataSource 顺序，那是用户主动操作，符合直觉，不冲突。
  const filteredRows = useMemo(() => {
    const matched = rows.filter((r) => {
      if (fPraise !== 'all') {
        const cur = praiseMap[r.id];
        if (fPraise === 'praise' && cur !== 'praise') return false;
        if (fPraise === 'no_praise' && cur !== 'no_praise') return false;
      }
      if (fRecogType && r.recognitionType !== fRecogType) return false;
      if (fNominator && r.nominator?.name !== fNominator) return false;
      if (fNominee && r.nominee?.name !== fNominee) return false;
      if (fDept && r.nominee?.deptPath !== fDept) return false;
      return true;
    });
    const head: RecognitionRow[] = [];
    const tail: RecognitionRow[] = [];
    for (const r of matched) {
      if (praiseMap[r.id] === 'no_praise') tail.push(r);
      else head.push(r);
    }
    return [...head, ...tail];
  }, [rows, praiseMap, fPraise, fRecogType, fNominator, fNominee, fDept]);

  const stats = useMemo(() => {
    const nominators = new Set(
      filteredRows.map((r) => r.nominator?.id ?? r.nominatorId),
    );
    const nominees = new Set(
      filteredRows.map((r) => r.nominee?.id ?? r.nomineeId),
    );
    return {
      total: filteredRows.length,
      nominators: nominators.size,
      nominees: nominees.size,
    };
  }, [filteredRows]);

  const [exportingXlsx, setExportingXlsx] = useState(false);

  const handleExportXlsx = async () => {
    if (rows.length === 0) {
      message.warning('本批次暂无表彰提名');
      return;
    }
    if (filteredRows.length === 0) {
      message.warning('当前筛选条件下没有可导出的记录');
      return;
    }
    if (!batchId) return;

    setExportingXlsx(true);
    try {
      const praiseBody: Record<string, string> = {};
      for (const k of Object.keys(praiseMap)) {
        const v = praiseMap[Number(k)];
        if (v === 'praise' || v === 'no_praise') praiseBody[k] = v;
      }

      const body = {
        batchId,
        nominationIds: filteredRows.map((r) => r.id),
        praiseMap: praiseBody,
        customColumns,
        customCells,
      };

      const resp = await http.post('/admin/recognitions/export.xlsx', body, {
        responseType: 'blob',
        timeout: 60_000,
      });
      const ct = resp.headers?.['content-type'] ?? '';
      if (!String(ct).includes('spreadsheetml')) {
        let text = '';
        try {
          text = await (resp.data as Blob).text();
        } catch {
          /* ignore */
        }
        throw new Error(text.slice(0, 280) || '后端未返回 Excel');
      }

      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const tagRaw =
        batchExportTag?.trim() || (batchId != null ? String(batchId) : 'batch');
      const tag = safeFilenameSegment(tagRaw);
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `表彰提名名单-${tag}-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success(`已导出 ${filteredRows.length} 条（与当前表格一致，已加水印）`);
    } catch (e: unknown) {
      message.error(extractErrMsg(e, '导出失败'));
    } finally {
      setExportingXlsx(false);
    }
  };

  if (!batchId) return null;

  return (
    <Card
      style={{ marginTop: 24 }}
      title={
        <Space size={8}>
          <TrophyOutlined style={{ color: '#fa8c16' }} />
          <span style={{ fontWeight: 600 }}>表彰人员</span>
          <Typography.Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
            （领导在「表彰提名」入口提交的汇总，只读）
          </Typography.Text>
        </Space>
      }
      extra={
        <Space size={12} wrap>
          <Tag color="orange">{stats.total} 条</Tag>
          <Tag color="geekblue">{stats.nominees} 人被表彰</Tag>
          <Tag color="purple">{stats.nominators} 位领导参与</Tag>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            disabled={!batchId}
            onClick={() => setManualMode({ mode: 'add' })}
          >
            + 添加表彰人员
          </Button>
          <Tooltip title="在表格末尾追加一列（如自订评语、跟进备注），仅保存在本浏览器">
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setColEditModal({ mode: 'add', title: '' })}
            >
              加列
            </Button>
          </Tooltip>
        </Space>
      }
    >
      {/* 筛选条：表彰 / 人才观 / 提名领导 / 被提名姓名 / 被提名部门 */}
      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space size={6}>
            <span style={{ color: '#666', fontSize: 13 }}>表彰</span>
            <Select<PraiseFlag | 'all'>
              size="small"
              value={fPraise}
              style={{ width: 110 }}
              onChange={(v) => setFPraise(v)}
              options={[
                { label: '全部', value: 'all' },
                { label: '表彰', value: 'praise' },
                { label: '不表彰', value: 'no_praise' },
              ]}
            />
          </Space>
        </Col>
        <Col>
          <Space size={6}>
            <span style={{ color: '#666', fontSize: 13 }}>人才观</span>
            <Select<string>
              size="small"
              value={fRecogType}
              style={{ width: 160 }}
              placeholder="全部"
              allowClear
              showSearch
              onChange={(v) => setFRecogType(v)}
              options={VALUES_10_LIST.map((v) => ({ label: v, value: v }))}
            />
          </Space>
        </Col>
        <Col>
          <Space size={6}>
            <span style={{ color: '#666', fontSize: 13 }}>提名领导</span>
            <Select<string>
              size="small"
              value={fNominator}
              style={{ width: 140 }}
              placeholder="全部"
              allowClear
              showSearch
              onChange={(v) => setFNominator(v)}
              options={nominatorOptions}
            />
          </Space>
        </Col>
        <Col>
          <Space size={6}>
            <span style={{ color: '#666', fontSize: 13 }}>被提名姓名</span>
            <Select<string>
              size="small"
              value={fNominee}
              style={{ width: 140 }}
              placeholder="全部"
              allowClear
              showSearch
              onChange={(v) => setFNominee(v)}
              options={nomineeOptions}
            />
          </Space>
        </Col>
        <Col>
          <Space size={6}>
            <span style={{ color: '#666', fontSize: 13 }}>被提名部门</span>
            <Select<string>
              size="small"
              value={fDept}
              style={{ width: 180 }}
              placeholder="全部"
              allowClear
              showSearch
              onChange={(v) => setFDept(v)}
              options={deptOptions}
            />
          </Space>
        </Col>
        <Col flex="auto" />
        <Col>
          <Tooltip title="导出当前表格所见名单为 Excel（含筛选、表彰标注、自订列；文件含水印，需登录且服务端校验数据）">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={exportingXlsx}
              onClick={() => void handleExportXlsx()}
            >
              导出
            </Button>
          </Tooltip>
        </Col>
      </Row>

      <Table<RecognitionRow>
        rowKey="id"
        size="small"
        dataSource={filteredRows}
        loading={isFetching}
        pagination={{ pageSize: 20, showTotal: (n) => `共 ${n} 条` }}
        scroll={{ x: 'max-content' }}
        rowClassName={(r) => {
          const flag = praiseMap[r.id];
          if (flag === 'praise') return 'tg-row-recog-praise';
          if (flag === 'no_praise') return 'tg-row-recog-no-praise';
          return '';
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                rows.length === 0
                  ? '本批次暂无表彰提名'
                  : '当前筛选条件下没有匹配的记录'
              }
            />
          ),
        }}
        columns={[
          {
            title: '表彰',
            key: 'praiseFlag',
            width: 96,
            align: 'center' as const,
            render: (_v, r) => (
              <Select<PraiseFlag>
                size="small"
                value={praiseMap[r.id]}
                allowClear
                placeholder="请选择"
                style={{ width: '100%' }}
                popupMatchSelectWidth={false}
                onChange={(v) => setPraise(r.id, v ?? null)}
                options={PRAISE_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: (
                    <span style={{ color: opt.color, fontWeight: 600 }}>
                      {opt.label}
                    </span>
                  ),
                }))}
              />
            ),
          },
          {
            title: '被表彰人',
            dataIndex: ['nominee', 'name'],
            width: 110,
            render: (v: string | null) => v ?? '—',
          },
          {
            title: '部门',
            dataIndex: ['nominee', 'deptPath'],
            ellipsis: true,
            render: (v: string | null) => v ?? '—',
          },
          {
            title: '职位',
            dataIndex: ['nominee', 'position'],
            width: 140,
            ellipsis: true,
            render: (v: string | null) => v ?? '—',
          },
          {
            title: '人才观',
            dataIndex: 'recognitionType',
            width: 130,
            render: (v: string | null) =>
              v ? (
                <Tag color={VALUES_10_COLOR[v] ?? 'default'}>{v}</Tag>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: '提名领导',
            dataIndex: ['nominator', 'name'],
            width: 110,
            render: (v: string | null) => v ?? '—',
          },
          {
            // 表彰理由：换行多行显示，把完整文字全部展开（农俊董事长 2026-05-12 要求：
            // "全部文字都能看到，不要一行，这样太长了"）。Antd Table 默认会单行省略，
            // 这里通过 render 返回带 whiteSpace: 'pre-wrap' 的 div 主动允许换行。
            title: '表彰理由',
            dataIndex: 'reason',
            width: 280,
            render: (v: string | null) =>
              v ? (
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.55,
                  }}
                >
                  {v}
                </div>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: '提交时间',
            dataIndex: 'submitAt',
            width: 160,
            render: (t: string | null) =>
              t ? new Date(t).toLocaleString('zh-CN', { hour12: false }) : '—',
          },
          {
            // 每行的操作列：编辑 / 删除（农俊董事长 2026-05-12 要求）
            // 删除走 Popconfirm 二次确认；编辑复用补录 Modal 切换为 edit 模式
            title: '操作',
            key: '__admin_actions',
            width: 110,
            align: 'center' as const,
            fixed: 'right' as const,
            render: (_v: unknown, r: RecognitionRow) => (
              <Space size={4}>
                <Tooltip title="编辑该条表彰">
                  <Button
                    size="small"
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => setManualMode({ mode: 'edit', row: r })}
                  />
                </Tooltip>
                <Tooltip title="删除该条表彰（物理删除，不可恢复）">
                  <Popconfirm
                    title={`删除「${r.nominee?.name ?? '该员工'}」的这条表彰？`}
                    description="将物理删除该记录，不可恢复。"
                    okText="删除"
                    cancelText="取消"
                    okType="danger"
                    onConfirm={() => removeMut.mutate(r.id)}
                  >
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      loading={removeMut.isPending && removeMut.variables === r.id}
                    />
                  </Popconfirm>
                </Tooltip>
              </Space>
            ),
          },
          // 自订列：固定在内置列之后，按追加顺序排列；每列宽 180，单元格是文本输入框，列头带 ▾ 菜单。
          ...customColumns.map<NonNullable<Parameters<typeof Table<RecognitionRow>>[0]['columns']>[number]>(
            (cc) => ({
              key: `custom_${cc.id}`,
              width: 180,
              title: (
                <Space size={4}>
                  <span>{cc.title}</span>
                  <Dropdown
                    trigger={['click']}
                    menu={{
                      items: [
                        {
                          key: 'rename',
                          label: '重命名此列',
                          icon: <EditOutlined />,
                        },
                        {
                          key: 'delete',
                          label: '删除此列',
                          icon: <DeleteOutlined />,
                          danger: true,
                        },
                      ],
                      onClick: ({ key, domEvent }) => {
                        domEvent.stopPropagation();
                        if (key === 'rename') {
                          setColEditModal({
                            mode: 'rename',
                            id: cc.id,
                            title: cc.title,
                          });
                        } else if (key === 'delete') {
                          Modal.confirm({
                            title: `删除列「${cc.title}」？`,
                            content:
                              '该列在所有行的内容将一并移除（无法恢复，仅删除本汇总卡片的自订列，不影响原始表彰记录）。',
                            okType: 'danger',
                            okText: '确认删除',
                            cancelText: '取消',
                            onOk: () => deleteCustomColumn(cc.id),
                          });
                        }
                      },
                    }}
                  >
                    <Tooltip title="重命名 / 删除该列">
                      <Button
                        size="small"
                        type="text"
                        icon={<DownOutlined style={{ fontSize: 10 }} />}
                        style={{ padding: '0 2px', height: 18 }}
                      />
                    </Tooltip>
                  </Dropdown>
                </Space>
              ),
              render: (_v: unknown, r: RecognitionRow) => (
                <Input.TextArea
                  value={customCells[r.id]?.[cc.id] ?? ''}
                  size="small"
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  placeholder="—"
                  onChange={(e) =>
                    updateCustomCell(r.id, cc.id, e.target.value)
                  }
                />
              ),
            }),
          ),
        ]}
      />

      {/* 「人力资源部补录 / 编辑表彰」Modal —— 同一个表单两种模式（add / edit） */}
      <Modal
        title={
          manualMode?.mode === 'edit'
            ? `编辑表彰：${manualMode.row.nominee?.name ?? ''}`
            : '人力资源部补录表彰人员'
        }
        open={manualOpen}
        onCancel={closeManualModal}
        okText={manualMode?.mode === 'edit' ? '保存' : '提交'}
        cancelText="取消"
        confirmLoading={manualMut.isPending}
        destroyOnClose
        onOk={async () => {
          try {
            const v = await manualForm.validateFields();
            if (manualMode?.mode === 'edit') {
              await manualEditMut.mutateAsync({
                id: manualMode.row.id,
                recognitionType: v.recognitionType || undefined,
                reason: v.reason,
                detailDesc: v.detailDesc || undefined,
              });
            } else {
              await manualAddMut.mutateAsync(v);
            }
          } catch {
            // 校验未过或请求失败由 message 提示
          }
        }}
      >
        <Form form={manualForm} layout="vertical" preserve={false}>
          <Form.Item
            label="被表彰人"
            name="nomineeId"
            rules={[{ required: true, message: '请选择被表彰人' }]}
            extra={
              manualMode?.mode === 'edit'
                ? '编辑模式下不可修改被表彰人；如需更换请删除本条后重新添加'
                : undefined
            }
          >
            <Select
              showSearch
              placeholder="搜索姓名 / 部门 / 职位"
              optionFilterProp="label"
              disabled={manualMode?.mode === 'edit'}
              options={[
                // 编辑模式下：把当前行的被表彰人作为一项注入到 options，
                // 否则 candidates 还没拉好或拉不到时，disabled 的 Select 会显示空白/value 数字
                ...(manualMode?.mode === 'edit'
                  ? [
                      {
                        value: manualMode.row.nomineeId,
                        label: `${manualMode.row.nominee?.name ?? '-'}${
                          manualMode.row.nominee?.deptPath
                            ? ` · ${manualMode.row.nominee.deptPath}`
                            : ''
                        }${
                          manualMode.row.nominee?.position
                            ? ` · ${manualMode.row.nominee.position}`
                            : ''
                        }`,
                      },
                    ]
                  : []),
                ...candidates
                  .filter(
                    (c) =>
                      // 编辑模式时把上面那条同 id 的 candidate 过滤掉，避免重复
                      manualMode?.mode !== 'edit' ||
                      c.id !== manualMode.row.nomineeId,
                  )
                  .map((c) => ({
                    value: c.id,
                    label: `${c.name}${c.deptPath ? ` · ${c.deptPath}` : ''}${
                      c.position ? ` · ${c.position}` : ''
                    }`,
                  })),
              ]}
              filterOption={(input, option) => {
                if (!option?.label) return false;
                return String(option.label).toLowerCase().includes(input.toLowerCase());
              }}
            />
          </Form.Item>
          <Form.Item
            label="符合的人才观"
            name="recognitionType"
            extra={'可选，选一项瑞小美人才观；不选则该条表彰仅按"理由"展示'}
          >
            <Select
              allowClear
              placeholder="请选择一项人才观（可选）"
              options={VALUES_10_FULL.map((v) => ({ label: v.value, value: v.value }))}
            />
          </Form.Item>
          <Form.Item
            label="表彰理由（必填）"
            name="reason"
            rules={[
              { required: true, message: '请输入表彰理由' },
              { max: 200, message: '理由不超过 200 字' },
            ]}
          >
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 6 }}
              placeholder="例：在 X 项目中独立交付 Y，体现「拿结果」"
              maxLength={200}
              showCount
            />
          </Form.Item>
          <Form.Item label="详细描述（可选）" name="detailDesc">
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 8 }}
              placeholder="可补充背景、影响范围、跨部门协作细节等"
              maxLength={500}
              showCount
            />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {manualMode?.mode === 'edit' ? (
              <>
                说明：admin 可修改任意一条表彰（含领导提交的）的 <strong>人才观 / 表彰理由 / 详细描述</strong>。
                被表彰人 / 提名人 / 批次 / 状态不可改——如需调整请删除后重新添加。
              </>
            ) : (
              <>
                说明：补录提名人显示为当前操作管理员，<strong>不占用</strong>任何表彰提名领导的名额；
                理由会自动加「【人力资源部补录】」前缀以示区分；提交后立即出现在本汇总表里。
              </>
            )}
          </Typography.Text>
        </Form>
      </Modal>

      {/* 添加 / 重命名 自订列 Modal */}
      <Modal
        title={
          colEditModal?.mode === 'add'
            ? '在表格末尾新增一列'
            : '重命名列'
        }
        open={!!colEditModal}
        onCancel={() => setColEditModal(null)}
        onOk={() => {
          if (!colEditModal) return;
          if (colEditModal.mode === 'add') {
            if (addCustomColumn(colEditModal.title)) setColEditModal(null);
          } else {
            if (renameCustomColumn(colEditModal.id, colEditModal.title))
              setColEditModal(null);
          }
        }}
        okText="确定"
        cancelText="取消"
        destroyOnClose
      >
        <Input
          autoFocus
          maxLength={20}
          showCount
          placeholder="请输入列名（不超过 20 字）"
          value={colEditModal?.title ?? ''}
          onChange={(e) =>
            setColEditModal((prev) =>
              prev ? { ...prev, title: e.target.value } : prev,
            )
          }
          onPressEnter={() => {
            if (!colEditModal) return;
            if (colEditModal.mode === 'add') {
              if (addCustomColumn(colEditModal.title)) setColEditModal(null);
            } else {
              if (renameCustomColumn(colEditModal.id, colEditModal.title))
                setColEditModal(null);
            }
          }}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          说明：自订列只在「表彰人员」汇总卡片显示，按当前批次保存到本浏览器；
          不影响原始表彰提名数据，也不会同步到服务器。
        </Typography.Text>
      </Modal>
    </Card>
  );
}
